import { useContext, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import AddComponent from "./add-component";
import { IAddEditProviderModel } from "../application-upsert-form";
import { ApplicationsContext } from "../applications-context";
import Typography from "@mui/material/Typography";
import { StyledButton } from "../../../components/styled-button/styled-button";

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: "0px",
  display: "flex",
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "var(--kyocera-dark-grey)",
  fontFamily: "Noto Sans",
  fontSize: "36px",
  fontWeight: "bolder",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const AddAppContainer = styled('div')(({ theme }) => ({
  "& button.MuiButton-root": {
    "&.Mui-disabled": {
      opacity: 0.5,
      backgroundColor: "var(--kyocera-bg-grey)",
      boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
      color: "var(--kyocera-text-grey)",
    },
    marginLeft: theme.spacing(2),
  },
}));

const AddApplicationComponent = () => {
  const [openAddApplication, setOpenAddApplication] = useState<boolean>(false);
  const { isAppListLoading, selectedApp, setSelectedApp} = useContext(ApplicationsContext);
  const { t } = useTranslation();

  const handleAddClickOpen = () => {
    setOpenAddApplication(true);

    if (selectedApp)
      setSelectedApp("");
  };

  const handleAddApplicationDone = () => {
    setOpenAddApplication(false);
  };

  return (
    <div>
      <AddAppContainer>
        <StyledButton
          variant="contained"
          onClick={handleAddClickOpen}
          className="d-flex align-items-center"
          disabled={isAppListLoading}
          startIcon={<AddCircleOutlineOutlinedIcon />}
        >
          {t("add_application")}
        </StyledButton>
      </AddAppContainer>
      <AddApplicationStep
        open={openAddApplication}
        handleAddApplicationClose={() => setOpenAddApplication(false)}
        handleAddApplicationDone={handleAddApplicationDone}
      />
    </div>
  );
};

type IAddApplicationProps = {
  open: boolean;
  handleAddApplicationClose: () => void;
  handleAddApplicationDone: () => void;
};

const AddApplicationStep = (props: IAddApplicationProps) => {
  const classes = {}; // Replace with styled component if needed
  const { t } = useTranslation();
  const { setUrlLoaded, setAppsLoaded, setSelectedApp, setSelectedCheckedApps } = useContext(ApplicationsContext);

  const defaultModel: IAddEditProviderModel = {
    0: {
      id: "",
      name: "",
      clientId: "",
      accessKey: ""
    },
    isValid: false
  };

  const [model, setModel] = useState<IAddEditProviderModel>(defaultModel);

  const handleClose = () => {
    setUrlLoaded(false);
    setAppsLoaded(false);
    setSelectedApp(null);
    setSelectedCheckedApps([]);
    props.handleAddApplicationClose();
    setModel(defaultModel);
  };

  const handleModelChange = (updatedModel: any) => {
    const updated = updatedModel;
    setModel((prevModel) => ({
      ...prevModel,
      ...updated,
    }));
  };

  useEffect(() => {
    return () => {
      setUrlLoaded(false);
    };
  }, [setUrlLoaded]);

  return (
    <Dialog
      open={props.open}
      PaperProps={{
        style: { maxWidth: '701px' }, // Set custom max width
          }}
      fullWidth={true}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("add_new_application")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <AddComponent
            handleDone={() => {
              handleClose();
              props.handleAddApplicationDone();
            }}
            handleClose={handleClose}
            model={model}
            handleModelChange={handleModelChange}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default AddApplicationComponent;
